import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './Navbar.module.css';

/*
 * The compass/chat-bubble logo icon used in the header brand link. Was
 * copy-pasted (identical path data) into every page that shows it.
 */
export const BrandIcon = ({ size = 24, style = {} }) => (
  <img src="/replyshoot_logo.png" alt="ReplyShoot" width="150" style={style} />
);

/*
 * Shared page-header <nav>. Every page still supplies its own look via
 * className/innerClassName/brandClassName (each page keeps its own CSS
 * module, so visual design is unchanged) - this component only owns the
 * structural skeleton that was being hand-copied into every page:
 * nav > (optional inner wrapper) > brand link > rest of the nav content.
 */
const Navbar = ({
  className,
  innerClassName,
  style,
  ariaLabel,
  brandTo = '/',
  brandClassName,
  brand,
  children,
  commonNavigation = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Reactive auth state — updates immediately after login/logout without a
  // full page reload, and also syncs across tabs via the storage event.
  const [isLoggedIn, setIsLoggedIn] = useState(
    typeof window !== 'undefined' && Boolean(localStorage.getItem('token1'))
  );

  useEffect(() => {
    const syncAuth = () => setIsLoggedIn(Boolean(localStorage.getItem('token1')));
    // 'storage' fires in other tabs; 'authchange' is our custom event for the same tab
    window.addEventListener('storage', syncAuth);
    window.addEventListener('authchange', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('authchange', syncAuth);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token1');
    localStorage.removeItem('user1');
    setIsLoggedIn(false);
    setMenuOpen(false);
    navigate('/');
  };

  const handleAuditClick = (e) => {
    setMenuOpen(false);
    if (location.pathname === '/audit') {
      const el = document.getElementById('audit-section') || document.getElementById('audit');
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth' });
        if (location.hash !== '#audit-section') {
          window.history.pushState(null, '', '/audit#audit-section');
        }
      }
    }
  };

  const brandLink = (
    <Link to={brandTo} className={`${styles.brandLink} ${brandClassName || ''}`} onClick={() => setMenuOpen(false)}>
      {brand}
    </Link>
  );
  const sharedLinks = (
    <>
      <a href="/#how-it-works" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>{t('howItWorks', 'How it Works')}</a>
      <a href="/#pricing" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>{t('pricing', 'Pricing')}</a>
      <Link to="/tools" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>{t('tools', 'Tools')}</Link>
      <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>{t('contact', 'Contact')}</Link>
      {isLoggedIn && <Link to="/dashboard" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>{t('dashboard', 'Dashboard')}</Link>}
      <Link
        to="/audit#audit-section"
        onClick={handleAuditClick}
        style={{ color: '#fff', background: '#9219a6', borderRadius: '8px', padding: '9px 15px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700 }}
      >
        {t('freeAudit', 'Free Audit')}
      </Link>
      <LanguageToggle />
      {!isLoggedIn && <Link to="/?login=true" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>{t('login', 'Login')}</Link>}
      {!isLoggedIn && <Link to="/?signup=true" style={{ color: '#fff', background: '#151222', borderRadius: '8px', padding: '9px 15px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700 }}>{t('signUp', 'Sign Up')}</Link>}
      {isLoggedIn && (
        <button
          onClick={handleLogout}
          style={{ color: 'inherit', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, padding: 0 }}
        >
          {t('logout', 'Logout')}
        </button>
      )}
    </>
  );

  const content = commonNavigation ? sharedLinks : children;

  return (
    <nav className={`${styles.navRoot} ${className || ''}`} style={style} aria-label={ariaLabel}>
      {innerClassName ? (
        <div className={`${styles.navInner} ${innerClassName}`}>
          {brandLink}
          <button
            type="button"
            className={styles.menuToggle}
            aria-expanded={menuOpen}
            aria-controls="site-navigation-menu"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {menuOpen ? <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></> : <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>}
            </svg>
          </button>
          <div id="site-navigation-menu" className={`${styles.menu} ${menuOpen ? styles.menuOpen : ''}`} onClick={() => setMenuOpen(false)}>
            {content}
          </div>
        </div>
      ) : (
        <>
          {brandLink}
          <button
            type="button"
            className={styles.menuToggle}
            aria-expanded={menuOpen}
            aria-controls="site-navigation-menu"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {menuOpen ? <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></> : <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>}
            </svg>
          </button>
          <div id="site-navigation-menu" className={`${styles.menu} ${menuOpen ? styles.menuOpen : ''}`} onClick={() => setMenuOpen(false)}>
            {content}
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
